// Guide §8.4 — Bible 14.3 (single source). A Snapshot test compares this table with Bible 14.3 (test/permissions.test.ts).
import { D, Decimal } from './money';
import type { Role } from './enums';

export const PERMISSIONS = {
  sale_entry: { owner: 1, manager: 1, cashier: 1 },
  stock_view_quantity: { owner: 1, manager: 1, cashier: 1 },
  stock_view_cost: { owner: 1, manager: 1, cashier: 0 },
  stock_edit: { owner: 1, manager: 1, cashier: 0 },
  vendor_purchase: { owner: 1, manager: 1, cashier: 0 },
  customer_edit: { owner: 1, manager: 1, cashier: 1 },
  baki_entry_collect: { owner: 1, manager: 1, cashier: 1 },
  baki_writeoff_doubtful: { owner: 1, manager: 0, cashier: 0 },
  daily_khata: { owner: 1, manager: 1, cashier: 0 }, // D1: was cashier=true before
  expenses: { owner: 1, manager: 1, cashier: 0 }, // B18: cashier doesn't even see it
  invoice_view_print: { owner: 1, manager: 1, cashier: 1 },
  invoice_void: { owner: 1, manager: 1, cashier: 0 }, // Manager: logged
  return_refund: { owner: 1, manager: 1, cashier: 0 },
  discount_apply: { owner: 1, manager: 1, cashier: 1 }, // within the limit
  business_notes: { owner: 1, manager: 1, cashier: 0 },
  stock_investment: { owner: 1, manager: 0, cashier: 0 },
  recycle_restore: { owner: 1, manager: 0, cashier: 0 },
  reports: { owner: 1, manager: 1, cashier: 0 },
  dukaniq_view: { owner: 1, manager: 1, cashier: 0 },
  sms_send_manual: { owner: 1, manager: 1, cashier: 0 },
  sms_buy: { owner: 1, manager: 0, cashier: 0 },
  import_export: { owner: 1, manager: 1, cashier: 0 },
  zakat: { owner: 1, manager: 0, cashier: 0 },
  printer_pairing: { owner: 1, manager: 1, cashier: 1 },
  review_queue: { owner: 1, manager: 0, cashier: 0 },
  business_settings: { owner: 1, manager: 0, cashier: 0 },
} as const satisfies Record<string, Record<Role, 0 | 1>>;

export type PermissionKey = keyof typeof PERMISSIONS;
export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

export const can = (role: Role, perm: PermissionKey): boolean => PERMISSIONS[perm][role] === 1;

/** Map of every permission for a role — what GET /me/capabilities returns. */
export function permissionMap(role: Role): Record<PermissionKey, boolean> {
  const out = {} as Record<PermissionKey, boolean>;
  for (const k of PERMISSION_KEYS) out[k] = can(role, k);
  return out;
}

export type DiscountLimit = { type: 'percent' | 'amount'; value: number | string };

export const DEFAULT_DISCOUNT_LIMIT = {
  manager: { type: 'percent', value: 10 },
  cashier: { type: 'percent', value: 5 }, // D1: was 3% before; 0 = disabled
} as const satisfies Record<'manager' | 'cashier', DiscountLimit>;

/**
 * Sum of all *hand-entered* discounts in one sale vs the staff limit (Bible 14.3).
 * Owner promotions don't count; owner has no limit.
 */
export function discountAllowed(role: Role, limit: DiscountLimit | null, gross: Decimal, manual: Decimal): boolean {
  if (role === 'owner') return true;
  const l = limit ?? DEFAULT_DISCOUNT_LIMIT[role];
  const v = D(l.value);
  if (v.isZero()) return manual.lte(0); // limit 0 means discounting is switched off
  return l.type === 'percent' ? manual.lte(gross.times(v).div(100)) : manual.lte(v);
}

/** Cashier home shows only their own work (Bible 13.5, DEC-33). */
export const CASHIER_SEES_COST = false;

/** Items hidden entirely (not locked) for a role in the "আরও" menu (Bible 13.4 / 14.3 rule: no lock icon for role denials). */
export const MORE_MENU_PERMISSION: Record<string, PermissionKey | null> = {
  customers: null,
  invoices: 'invoice_view_print',
  daily_khata: 'daily_khata',
  expenses: 'expenses',
  stock_investment: 'stock_investment',
  vendors: 'vendor_purchase',
  profit_promotions: 'reports',
  dukaniq: 'dukaniq_view',
  zakat: 'zakat',
  reports: 'reports',
  notes: 'business_notes',
  new_shop: 'business_settings',
  import_export: 'import_export',
  sms_points: 'sms_send_manual',
  review_queue: 'review_queue',
  settings: null,
  support: null,
  recycle_bin: 'recycle_restore',
};
